import { CommonModule } from '@angular/common';
import { Component, inject, input, signal, TemplateRef, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ContactService } from '../../services/contact.service';
import { SalesforceService } from '../../services/salesforce.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-general-contact',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
  ],
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: 'es-MX' }],
  templateUrl: './general-contact.component.html',
  styleUrl: './general-contact.component.scss',
})
export class GeneralContactComponent {
  private fb = inject(FormBuilder);
  private contactService = inject(ContactService);
  private salesforceService = inject(SalesforceService);

  readonly loading = signal(false);
  readonly errorSend = signal(false);
  readonly submitted = signal(false);
  readonly isRental = signal(false);
  readonly showInterestField = signal(true);

  readonly states = signal<any[]>([]);
  readonly municipalities = signal<any>({});
  readonly selectedMunicipalities = signal<any[]>([]);

  readonly namePage = input<string>('');
  readonly thanksPage = input<string>('');
  readonly interested = input<boolean>(true);
  readonly interestedValue = input<string | null>(null);

  @ViewChild('thanks') thanks!: TemplateRef<any>;

  form = this.fb.group({
    web_nombres__c: ['', Validators.required],
    web_Apellidos__c: ['', Validators.required],
    web_email__c: ['', [Validators.required, Validators.email]],
    web_empresa__c: ['', Validators.required],
    web_estado__c: ['', Validators.required],
    web_municipio__c: ['', Validators.required],
    web_telefono__c: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
    web_cp__c: ['', Validators.required],
    web_area_interes__c: ['', Validators.required],
    web_tyc__c: [false, Validators.requiredTrue],
    web_boletin__c: [false],
    Description: [''],
    RecordTypeId: [''],
    web_interesado_en__c: [''],
    web_pa__c: [''],
    TSC_Compania__c: ['Tracsa'],
    TSC_InicioRenta__c: [''],
    TSC_DuracionRenta__c: [''],
    TSC_Empresa__c: [{ value: '', disabled: true }],
    TSC_Telefono__c: [{ value: '', disabled: true }],
    Lastname: [{ value: '', disabled: true }],
    Company: [{ value: '', disabled: true }],
    LeadSource: [{ value: '', disabled: true }],
    TSC_Fuente__c: [{ value: '', disabled: true }],
    URL_P_gina_origen__c: [{ value: '', disabled: true }],
  });

  readonly interestList = [
    'Venta de equipo nuevo',
    'Venta de equipo usado',
    'Renta de equipo',
    'Venta de refacciones',
    'Servicio',
    'Capacitación',
  ];

  readonly caseList = ['Bolsa de trabajo', 'Proveedores', 'Quejas y sugerencias'];

  readonly minDate = new Date();

  constructor() {
    this.loadStates();
    this.loadMunicipalities();
  }

  saveForm(): void {
    this.submitted.set(true);

    if (!this.interested) {
      this.form.patchValue({ web_interesado_en__c: this.namePage() });
    }

    if (this.form.invalid) return;

    if (!this.form.value.TSC_InicioRenta__c) {
      this.form.controls.TSC_InicioRenta__c.disable();
      this.form.controls.TSC_DuracionRenta__c.disable();
    }

    this.form.patchValue({ web_pa__c: this.namePage() });
    this.submit();
  }

  private submit(): void {
    this.loading.set(true);

    this.salesforceService.generateToken().subscribe({
      next: ({ token }) => {
        sessionStorage.setItem('token', token);
        if (this.isCase()) {
          this.prepareCase();
        } else {
          this.prepareLead();
        }
      },
      error: () => this.errorSend.set(true),
    });
  }

  private isCase(): boolean {
    return this.caseList.includes(this.form.value.web_area_interes__c ?? '');
  }

  private prepareCase(): void {
    this.form.patchValue({
      RecordTypeId: environment.rtCase,
    });
    this.form.controls.TSC_Empresa__c.enable();
    this.form.controls.TSC_Telefono__c.enable();
    this.form.patchValue({
      TSC_Empresa__c: this.form.value.web_empresa__c,
      TSC_Telefono__c: this.form.value.web_telefono__c,
    });
    this.saveCase();
  }

  private saveCase(): void {
    this.salesforceService.saveCase(this.form.value).subscribe({
      next: (res) => this.onSuccess(res.id),
      error: () => this.errorSend.set(true),
    });
  }

  private prepareLead(): void {
    this.form.controls.Lastname.enable();
    this.form.controls.Company.enable();
    this.form.controls.LeadSource.enable();
    this.form.controls.TSC_Fuente__c.enable();
    this.form.controls.URL_P_gina_origen__c.enable();
    this.form.patchValue({
      RecordTypeId: environment.rtLead,
      Lastname: this.form.value.web_Apellidos__c,
      Company: this.form.value.web_empresa__c,
      LeadSource: 'Marketing',
      TSC_Fuente__c: 'Página Web',
      URL_P_gina_origen__c: window.location.href,
    });

    this.saveLead();
  }

  private saveLead(): void {
    this.salesforceService.saveLead(this.form.value).subscribe({
      next: (res) => this.onSuccess(res.id),
      error: () => this.errorSend.set(true),
    });
  }

  private onSuccess(_id: string): void {
    this.loading.set(false);
    window.location.href = `/gracias/${this.thanksPage()}`;
  }

  loadStates(): void {
    this.contactService.getStates().subscribe((r) => this.states.set(r));
  }

  loadMunicipalities(): void {
    this.contactService.getMunicipalities().subscribe((r) => this.municipalities.set(r));
  }

  selectState(event: any): void {
    this.selectedMunicipalities.set(this.municipalities()[event.value] ?? []);
  }

  validQuote(event: any): void {
    const value = event.target.value === 'Renta de equipo';
    const start = this.form.get('TSC_InicioRenta__c');
    const duration = this.form.get('TSC_DuracionRenta__c');

    if (value) {
      this.isRental.set(true);
      start?.setValidators(Validators.required);
      duration?.setValidators(Validators.required);
    } else {
      this.isRental.set(false);
      start?.clearValidators();
      duration?.clearValidators();
    }

    start?.updateValueAndValidity();
    duration?.updateValueAndValidity();
  }

  onlyNumberKey(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D+/g, '');
  }
}
